import React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Tooltip } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { UserIdentity } from '@/types/user';
import { getDocumentAccess } from './documentAccess';
import type { VisibilityChipProps } from '../types';

/**
 * VisibilityChip - who can see this document, from the signed-in user's view
 *
 * - Own document, nobody else: "Private" with a lock
 * - Own document, shared with users or groups: "Shared" with a share icon and
 *   a tooltip that counts the recipients
 * - Someone else's document that reached the user through a share:
 *   "Shared with me" with the owner in the tooltip
 * - Organization and public stay as before, with their own icons
 *
 * @param visibility - The visibility level ('public', 'organization', 'private', 'group')
 * @param doc - The document; enables share and ownership detection
 * @param currentUserId - Id of the signed-in user
 */
export const VisibilityChip: React.FC<VisibilityChipProps> = ({ visibility, doc, currentUserId, currentUserGroupIds }) => {
  const { t } = useTranslation();
  const access = doc ? getDocumentAccess(doc, currentUserId, currentUserGroupIds) : null;
  const sx = { cursor: 'default' } as const;

  if (access?.isSharedWithMe && (visibility === 'private' || visibility === 'group')) {
    const owner = doc ? UserIdentity.fromDocument(doc as Parameters<typeof UserIdentity.fromDocument>[0]) : null;
    const ownerName = owner && !owner.isEmpty() ? owner.getDisplayName() : t('documents.permissions.unknown');
    // Direct share, share through one of the user's groups, or unknown route
    // (group list not loaded yet, or access granted by role or organization)
    const viaGroup = access.viaGroupNames.length > 0;
    const hint = access.viaDirect
      ? t('documents.visibility.sharedByOwnerDirect', { owner: ownerName })
      : viaGroup
        ? t('documents.visibility.sharedByOwnerGroup', { owner: ownerName, group: access.viaGroupNames.join(', ') })
        : t('documents.visibility.sharedByOwner', { owner: ownerName });
    const icon = access.viaDirect
      ? <PersonOutlineOutlinedIcon />
      : viaGroup
        ? <GroupsOutlinedIcon />
        : <FolderSharedOutlinedIcon />;
    const label = !access.viaDirect && viaGroup
      ? t('documents.visibility.sharedWithYouViaGroup')
      : t('documents.visibility.sharedWithYou');
    return (
      <Tooltip arrow title={hint}>
        <Chip
          icon={icon}
          label={label}
          color="info"
          variant="outlined"
          size="small"
          sx={sx}
        />
      </Tooltip>
    );
  }

  if (access?.hasShares && (visibility === 'private' || visibility === 'group')) {
    const parts: string[] = [];
    if (access.userCount > 0) {
      parts.push(t('documents.visibility.persons', { count: access.userCount }));
    }
    if (access.groupCount > 0) {
      parts.push(t('documents.visibility.groupsPart', { count: access.groupCount }));
    }
    return (
      <Tooltip arrow title={t('documents.visibility.sharedSummary', { recipients: parts.join(t('documents.visibility.and')) })}>
        <Chip
          icon={<ShareOutlinedIcon />}
          label={t('documents.visibility.sharedByYou')}
          color="primary"
          variant="outlined"
          size="small"
          sx={sx}
        />
      </Tooltip>
    );
  }

  if (visibility === 'public') {
    return <Chip icon={<PublicOutlinedIcon />} label={t('documents.visibility.public')} color="success" size="small" sx={sx} />;
  }
  if (visibility === 'organization') {
    return <Chip icon={<BusinessOutlinedIcon />} label={t('documents.visibility.organization')} color="secondary" size="small" sx={sx} />;
  }
  if (visibility === 'group') {
    return <Chip icon={<GroupsOutlinedIcon />} label={t('documents.visibility.group')} color="default" size="small" sx={sx} />;
  }
  if (visibility === 'private') {
    return (
      <Tooltip arrow title={t('documents.visibility.privateHint')}>
        <Chip icon={<LockOutlinedIcon />} label={t('documents.visibility.privateShort')} color="default" size="small" sx={sx} />
      </Tooltip>
    );
  }
  return <Chip label={visibility} color="default" size="small" sx={sx} />;
};

VisibilityChip.displayName = 'VisibilityChip';
